// InboxPage.duck.js
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import {
  getAllTransitionsForEveryProcess,
  getOfferAndInquiryTransitions,
} from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { removePaymentTransitions } from '../../util/genericHelpers';

// ================ Helpers ================ //

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Action types ================ //

// Messages slice
export const FETCH_MESSAGES_REQUEST = 'app/InboxPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/InboxPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/InboxPage/FETCH_MESSAGES_ERROR';

// Notifications slice
export const FETCH_NOTIFICATIONS_REQUEST = 'app/InboxPage/FETCH_NOTIFICATIONS_REQUEST';
export const FETCH_NOTIFICATIONS_SUCCESS = 'app/InboxPage/FETCH_NOTIFICATIONS_SUCCESS';
export const FETCH_NOTIFICATIONS_ERROR = 'app/InboxPage/FETCH_NOTIFICATIONS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Messages
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  messagesPagination: null,
  messagesTransactionRefs: [],

  // Notifications
  fetchNotificationsInProgress: false,
  fetchNotificationsError: null,
  notificationsPagination: null,
  notificationsTransactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    // ----- Messages -----
    case FETCH_MESSAGES_REQUEST:
      return {
        ...state,
        fetchMessagesInProgress: true,
        fetchMessagesError: null,
      };

    case FETCH_MESSAGES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchMessagesInProgress: false,
        fetchMessagesError: null,
        messagesTransactionRefs: entityRefs(transactions),
        messagesPagination: payload.data.meta,
      };
    }

    case FETCH_MESSAGES_ERROR:
      console.error(payload); // eslint-disable-line
      return {
        ...state,
        fetchMessagesInProgress: false,
        fetchMessagesError: payload,
      };

    // ----- Notifications -----
    case FETCH_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        fetchNotificationsInProgress: true,
        fetchNotificationsError: null,
      };

    case FETCH_NOTIFICATIONS_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchNotificationsInProgress: false,
        fetchNotificationsError: null,
        notificationsTransactionRefs: entityRefs(transactions),
        notificationsPagination: payload.data.meta,
      };
    }

    case FETCH_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return {
        ...state,
        fetchNotificationsInProgress: false,
        fetchNotificationsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

// Messages
const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = response => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: response,
});
const fetchMessagesError = e => ({
  type: FETCH_MESSAGES_ERROR,
  error: true,
  payload: e,
});

// Notifications
const fetchNotificationsRequest = () => ({ type: FETCH_NOTIFICATIONS_REQUEST });
const fetchNotificationsSuccess = response => ({
  type: FETCH_NOTIFICATIONS_SUCCESS,
  payload: response,
});
const fetchNotificationsError = e => ({
  type: FETCH_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

// NOTE: loadData now always fetches BOTH:
// - messages: only offer/inquiry transitions
// - notifications: all transitions
export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  dispatch(fetchMessagesRequest());
  dispatch(fetchNotificationsRequest());

  const { page = 1 } = parse(search || '');

  const baseQueryParams = {
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  const messagesQueryParams = {
    ...baseQueryParams,
    lastTransitions: getOfferAndInquiryTransitions(),
  };


  
  const notificationsQueryParams = {
    ...baseQueryParams,
    lastTransitions: removePaymentTransitions(getAllTransitionsForEveryProcess()),
  };


  try {
    // Fetch both in parallel
    const [messagesResponse, notificationsResponse] = await Promise.all([
      sdk.transactions.query(messagesQueryParams),
      sdk.transactions.query(notificationsQueryParams),
    ]);

    // Store entities from both responses
    dispatch(addMarketplaceEntities(messagesResponse));
    dispatch(fetchMessagesSuccess(messagesResponse));

    dispatch(addMarketplaceEntities(notificationsResponse));
    dispatch(fetchNotificationsSuccess(notificationsResponse));

    return { messages: messagesResponse, notifications: notificationsResponse };
  } catch (e) {
    const error = storableError(e);
    dispatch(fetchMessagesError(error));
    dispatch(fetchNotificationsError(error));
    throw e;
  }
};
